const TermsOfService = () => {
  return (
    <div className="p-8 max-w-4xl mx-auto text-gray-800 leading-relaxed">
      <h1 className="text-3xl font-bold mb-4">Terms of Service</h1>
      <p><strong>Effective Date:</strong> 2025-06-18</p>
      <p><strong>Company:</strong> JRAutomation — BCE 1023951695</p>
      <p><strong>Contact:</strong> info@jrautomation.be</p>

      <h2 className="text-2xl font-semibold mt-6">1. Acceptance of Terms</h2>
      <p>
        By accessing or using any services provided by JRAutomation ("we", "us", "our"), you agree to be bound by these Terms of Service. If you do not agree, please do not use our services.
      </p>

      <h2 className="text-2xl font-semibold mt-6">2. Services Provided</h2>
      <p>We offer automation, integration, and consulting services using platforms such as n8n, WhatsApp, and third-party APIs. These services may include (but are not limited to):</p>
      <ul className="list-disc ml-6">
        <li>Automation setup</li>
        <li>Flow development and deployment</li>
        <li>Technical consulting and support</li>
        <li>Chatbot and CRM integrations</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-6">3. Client Obligations</h2>
      <ul className="list-disc ml-6">
        <li>Provide accurate, complete information for project execution</li>
        <li>Maintain proper access credentials and security of your systems</li>
        <li>Comply with all applicable local and international laws</li>
        <li>Respect third-party platform terms (e.g., WhatsApp Business API, n8n Cloud)</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-6">4. Pricing and Payment</h2>
      <ul className="list-disc ml-6">
        <li>Services are invoiced based on scope (fixed project fee or hourly rate)</li>
        <li>External infrastructure costs (e.g. API keys, cloud hosting, licenses) are billed separately or directly to the client</li>
        <li>Payments must be made within 14 days unless otherwise agreed</li>
        <li>Delays may result in suspension of service or late fees</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-6">5. Intellectual Property</h2>
      <p>All automation flows and code developed by JRAutomation remain our property until full payment is received.</p>
      <p>After payment, the client is granted a non-exclusive, non-transferable right to use the deliverables for their business.</p>
      <p>Prebuilt templates, tools, and scripts may remain proprietary unless explicitly transferred.</p>

      <h2 className="text-2xl font-semibold mt-6">6. Liability & Disclaimers</h2>
      <p>We provide our services "as is", with best effort and professional diligence.</p>
      <p>We are not responsible for data loss, third-party platform issues, or downtime caused by changes outside our control.</p>
      <p>Total liability is limited to the amount paid by the client for the specific service.</p>

      <h2 className="text-2xl font-semibold mt-6">7. Termination</h2>
      <p>Either party may terminate the agreement with written notice. Upon termination:</p>
      <ul className="list-disc ml-6">
        <li>Any outstanding fees remain due</li>
        <li>Client access to hosted solutions or support may be revoked</li>
        <li>No refunds are provided for completed phases of work</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-6">8. Confidentiality</h2>
      <p>Both parties agree to keep confidential all business and technical information exchanged unless required by law.</p>

      <h2 className="text-2xl font-semibold mt-6">9. Privacy</h2>
      <p>
        We process your data in accordance with our{" "}
        <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>. For questions, contact info@jrautomation.be.
      </p>

      <h2 className="text-2xl font-semibold mt-6">10. Applicable Law and Jurisdiction</h2>
      <p>These terms are governed by Belgian law. Any disputes shall be submitted to the courts of Brussels, unless otherwise agreed.</p>

      <hr className="my-8" />

      <h2 className="text-2xl font-semibold mt-6">Annex: Data Consent Agreement</h2>
      <p>This section provides a sample consent agreement for clients who choose to share confidential business data with JRAutomation as part of an automation analysis project.</p>

      <h3 className="text-xl font-bold mt-4">ENGLISH</h3>
      <p><strong>DATA CONSENT AGREEMENT</strong></p>
      <ul className="list-disc ml-6">
        <li>The Client agrees to share confidential data (e.g., workflows, documentation, internal processes) for analysis within the scope of this project.</li>
        <li>The data will be used exclusively by JRAutomation for business analysis and automation strategy design.</li>
        <li>No data will be shared with third parties or reused in other projects without explicit written consent.</li>
        <li>JRAutomation will act as the Data Controller under the GDPR.</li>
        <li>This consent includes an on-site visit to the Client's business location to observe operational processes.</li>
        <li>The processing and storage of data will comply with Belgian data protection legislation.</li>
        <li>This agreement may be signed digitally or on paper.</li>
      </ul>

      <h3 className="text-xl font-bold mt-6">NEDERLANDS</h3>
      <p><strong>TOESTEMMINGSFORMULIER VOOR GEGEVENSVERWERKING</strong></p>
      <ul className="list-disc ml-6">
        <li>De Klant stemt ermee in om vertrouwelijke gegevens te delen (zoals werkprocessen, documenten, interne procedures) voor analyse binnen het kader van dit project.</li>
        <li>De gegevens zullen uitsluitend worden gebruikt door JRAutomation voor bedrijfsanalyse en ontwerp van automatiseringsstrategieën.</li>
        <li>De gegevens zullen niet worden gedeeld met derden of hergebruikt zonder uitdrukkelijke schriftelijke toestemming.</li>
        <li>JRAutomation treedt op als Verwerkingsverantwoordelijke volgens de GDPR.</li>
        <li>Deze toestemming omvat een bezoek ter plaatse aan het bedrijf van de Klant om operationele processen te observeren.</li>
        <li>De verwerking en opslag van gegevens gebeurt conform de Belgische privacywetgeving.</li>
        <li>Deze overeenkomst kan digitaal of op papier worden ondertekend.</li>
      </ul>

      <h3 className="text-xl font-bold mt-6">ESPAÑOL</h3>
      <p><strong>FORMULARIO DE CONSENTIMIENTO DE DATOS</strong></p>
      <ul className="list-disc ml-6">
        <li>La Clienta acepta compartir datos confidenciales (por ejemplo, flujos de trabajo, documentación, procesos internos) para su análisis en el marco de este proyecto.</li>
        <li>Los datos serán utilizados exclusivamente por JRAutomation para el análisis del negocio y el diseño de una estrategia de automatización.</li>
        <li>Los datos no serán compartidos con terceros ni reutilizados en otros proyectos sin autorización escrita expresa.</li>
        <li>JRAutomation actuará como Responsable del Tratamiento de acuerdo con el GDPR.</li>
        <li>Este consentimiento incluye una visita presencial al negocio para observar los procesos operativos.</li>
        <li>El tratamiento y almacenamiento de los datos cumplirá con la legislación belga en materia de protección de datos.</li>
        <li>Este acuerdo podrá firmarse en papel o por medios digitales.</li>
      </ul>

      <p className="mt-6 italic">Last updated: 2025-06-18</p>
    </div>
  );
};

export default TermsOfService;